import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ContextMenu, renderContextMenuItems } from "@air/primitive-context-menu";
import { tailwindMerge } from "@air/tailwind-variants";
import { Slot } from "@radix-ui/react-slot";
import { memo, useCallback, useState } from "react";
import { GallerySelectableCardTrigger } from "~/components/v2/components/shared/gallery/GallerySelectableCardTrigger";
import { useSelectableCard } from "~/hooks/useSelectableCard";
import { useSelectionAwareContextMenuOptions } from "~/hooks/useSelectionAwareContextMenuOptions";
const _GallerySelectableCard = (param)=>{
    let { asChild, children, className, getItemMenuOptions, getSelectionMenuOptions, isSelectable, item, onClick, onMouseMove, onMouseLeave } = param;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { onBlur, onFocus, handleHover, isHovering, handleItemClick, handleItemDoubleClick, handleUnhover, confirmItemSelection, preSelectItem, itemRef, isSelected, isHighlighted } = useSelectableCard({
        item,
        onClick,
        isSelectable
    });
    const { options } = useSelectionAwareContextMenuOptions({
        contextMenuOptions: getItemMenuOptions({
            trackLocation: "gallery-item-context-menu"
        }),
        id: item.id,
        selectionMenuOptions: getSelectionMenuOptions("gallery-multi-select-context-menu")
    });
    const Component = asChild ? Slot : "div";
    const handleMouseEnter = useCallback(()=>{
        handleHover();
    }, [
        handleHover
    ]);
    const handleMouseMove = useCallback((event)=>{
        handleMouseEnter();
        onMouseMove === null || onMouseMove === void 0 ? void 0 : onMouseMove(event);
    }, [
        handleMouseEnter,
        onMouseMove
    ]);
    const handleMouseLeave = useCallback((event)=>{
        handleUnhover();
        onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave(event);
    }, [
        handleUnhover,
        onMouseLeave
    ]);
    const handleKeyUp = useCallback((event)=>{
        if (event.key === "Enter" || event.key === " ") {
            handleItemClick();
        }
    }, [
        handleItemClick
    ]);
    /**
   * If the menu is open, we want to keep the hover state active
   */ const _isHovering = isHovering || isMenuOpen;
    return /*#__PURE__*/ _jsx(ContextMenu, {
        trigger: /*#__PURE__*/ _jsxs(Component, {
            className: tailwindMerge("group/gallery-selectable-card", className),
            "data-highlighted": isHighlighted,
            "data-hovering": isHovering,
            "data-is-selectable": isSelectable,
            "data-selected": isSelected,
            "data-testid": "GALLERY_SELECTABLE_CARD",
            onBlur: onBlur,
            onFocus: onFocus,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            onMouseMove: handleMouseMove,
            ref: itemRef,
            children: [
                /*#__PURE__*/ _jsx(GallerySelectableCardTrigger, {
                    confirmItemSelection: confirmItemSelection,
                    isHighlighted: isHighlighted || isSelected,
                    isHovering: _isHovering,
                    isSelectable: isSelectable,
                    onClick: onClick,
                    onKeyUp: handleKeyUp,
                    onDoubleClick: handleItemDoubleClick,
                    preSelectItem: preSelectItem
                }),
                children({
                    setIsMenuOpen,
                    isMenuOpen,
                    onBlur,
                    onFocus,
                    handleHover,
                    isHovering: _isHovering,
                    handleItemClick,
                    handleItemDoubleClick,
                    handleUnhover,
                    confirmItemSelection,
                    preSelectItem,
                    itemRef: itemRef,
                    isSelected,
                    isHighlighted
                })
            ]
        }),
        children: renderContextMenuItems({
            options
        })
    });
};
export const GallerySelectableCard = /*#__PURE__*/ memo(_GallerySelectableCard);
